/**
 * Custom hook for chat functionality
 */
import { useAppDispatch, useAppSelector } from "../redux";

import {
  sendMessage,
  uploadFileInChat,
  fetchSessions,
  fetchHistory,
  deleteSession,
  closeSession,
  addUserMessage,
  clearMessages,
  setCurrentSession,
  startNewSession,
  clearPendingSession,
} from "../redux/slices/chatSlice";

import { ChatSession } from "../types/chat.types";

export const useChat = () => {
  const dispatch = useAppDispatch();
  const {
    currentSession,
    sessions,
    messages,
    loading,
    sending,
    uploading,
    error,
    totalSessions,
    pendingSessionId,
  } = useAppSelector((state) => state.chat);

  const handleSendMessage = async (
    content: string,
    session_id?: string,
    llmConfig?: { llm_provider?: string; llm_model?: string }
  ) => {
    // Use provided session_id, OR pendingSessionId for new chats, OR currentSession.id
    const effectiveSessionId = session_id || pendingSessionId || currentSession?.id;
    
    // Optimistically add user message
    dispatch(addUserMessage({ content, session_id: effectiveSessionId || "pending" }));
    
    const result = await dispatch(
      sendMessage({ content, session_id: effectiveSessionId, ...llmConfig })
    );
    
    // Clear pendingSessionId after first message (session now created)
    if (pendingSessionId && result.meta.requestStatus === 'fulfilled') {
      dispatch(clearPendingSession());
    }
    
    return result;
  };

  const handleUploadFile = async (
    file: File,
    session_id: string,
    job_type?: string
  ) => {
    const result = await dispatch(
      uploadFileInChat({ file, session_id, job_type })
    );
    return result;
  };

  const handleFetchSessions = async (
    page: number = 1,
    page_size: number = 20
  ) => {
    await dispatch(fetchSessions({ page, page_size }));
  };

  const handleFetchHistory = async (session_id: string) => {
    await dispatch(fetchHistory(session_id));
  };

  const handleDeleteSession = async (session_id: string) => {
    await dispatch(deleteSession(session_id));
  };

  const handleCloseSession = async (session_id: string) => {
    await dispatch(closeSession(session_id));
  };

  const handleStartNewChat = () => {
    dispatch(startNewSession());  // Generates new UUID and clears state
  };

  const handleSelectSession = async (session: ChatSession) => {
    dispatch(setCurrentSession(session));
    await handleFetchHistory(session.id);
  };

  return {
    currentSession,
    sessions,
    messages,
    loading,
    sending,
    uploading,
    error,
    totalSessions,
    sendMessage: handleSendMessage,
    uploadFile: handleUploadFile,
    fetchSessions: handleFetchSessions,
    fetchHistory: handleFetchHistory,
    deleteSession: handleDeleteSession,
    closeSession: handleCloseSession,
    startNewChat: handleStartNewChat,
    selectSession: handleSelectSession,
  };
};
